import React, { useEffect } from "react"
import { useTranslation } from "next-i18next"
import { compareWallet, formatTwitterLink, shortenHex, tryGetErrorMessage } from "../../utils/string"
import { CheckCircleSvg, DiscordSvg, EmailSvg, PencilSvg, TwitterSvg, WalletSvg } from "../../assets/svg"
import { Copy } from "../shared/Copy"
import useGlobalStore from "../../hooks/useGlobalStore"
import { bindWalletAddress } from "../../services/account"
import { useAccount, useConnect, useWalletClient } from "wagmi"
import { ConnectButton } from "@rainbow-me/rainbowkit"

interface AccountInfoProps {
    onEdit: () => void

    profile: any
}

export const AccountInfo = ({ profile, onEdit }: AccountInfoProps) => {
    const { t: tCommon } = useTranslation("common")
    const { t: tMyAccount } = useTranslation("my-account")

    const { user, setUser, setPopupMessage, isValidAccount, isLoggedIn } = useGlobalStore()

    const { address: account, isDisconnected, isConnected } = useAccount()
    const { data: walletClient } = useWalletClient()
    const { connect } = useConnect()

    const { name, email, profileImage, discord, twitter, walletAddress } = profile

    const onConnect = async (connector, provider) => {
        if (isLoggedIn && compareWallet(user.walletAddress, account)) {
            // @ts-ignore
            await window.ethereum.request({
                method: "wallet_requestPermissions",
                params: [
                    {
                        eth_accounts: {}
                    }
                ]
            })
        }

        window.localStorage.setItem("provider", provider)

        connect()
    }

    const onBindWalletAddress = async () => {
        try {
            const response = await bindWalletAddress(account, walletClient)
            if (response.status === 200) {
                setUser({
                    ...user,
                    walletAddress: account
                })
            }
        } catch (ex: any) {
            if (ex.code !== 4001 && ex.code !== -32603) {
                setPopupMessage({
                    message: tryGetErrorMessage(ex)
                })
            }
        }
    }

    useEffect(() => {
        setUser({
            ...user,
            profileImage
        })
    }, [])

    useEffect(() => {
        if (isConnected && walletClient && !walletAddress) {
            onBindWalletAddress()
        }
    }, [walletClient, isDisconnected, account])

    return (
        <div
            className={
                "mt-5 flex flex-col justify-between rounded-[20px] bg-[#F2F2F2] py-[20px] px-[34px] lg:h-[225px] lg:flex-row"
            }
        >
            <div className={"flex flex-col items-center justify-center text-12 lg:flex-row lg:items-start lg:text-14"}>
                <div
                    className={
                        "h-[113px] w-[113px] overflow-hidden rounded-full bg-[url(../assets/imgs/bg/profile.png)] bg-cover lg:h-[145px] lg:w-[145px]"
                    }
                >
                    {profileImage && <img src={profileImage} className={"h-full w-full object-cover"} />}
                </div>
                <div className={"lg:ml-[38px] lg:mt-0"}>
                    <div className={"mt-5 text-center text-20 font-bold leading-6 lg:mt-0 lg:text-left"}>{name}</div>
                    <div className={"mt-[18px] flex"}>
                        <div className={"flex min-w-[160px] items-center"}>
                            <div className={"b-flex-center mr-3 h-[20px] w-[20px]"}>
                                <WalletSvg
                                    width={16}
                                    height={14}
                                    className={"fill-[#546F7A] last-child:fill-[#546F7A]"}
                                />
                            </div>
                            {tMyAccount("walletAddress")}:
                        </div>
                        <div className={"flex items-center font-medium"}>
                            <div className={"mr-[10px] text-accent-4"}>
                                {isValidAccount ? (
                                    shortenHex(account, 5)
                                ) : isLoggedIn && user.walletAddress ? (
                                    shortenHex(user.walletAddress, 5)
                                ) : (
                                    <div
                                        className={
                                            "b-flex-center sign-in-button h-[23px] w-[63px] rounded-[4px] border border-[#F5B826] text-14 text-black"
                                        }
                                    >
                                        {!account ? (
                                            <ConnectButton label={tCommon("menu.signIn")} />
                                        ) : (
                                            tCommon("menu.signIn")
                                        )}
                                    </div>
                                )}
                            </div>
                            <Copy text={account} iconClassName={"w-[12px] h-[14px]"} />
                        </div>
                    </div>
                    <div className={"mt-5 flex"}>
                        <div className={"flex min-w-[160px] items-center"}>
                            <div className={"b-flex-center mr-3 h-[20px] w-[20px]"}>
                                <EmailSvg width={16} height={12} className={"fill-[#546F7A]"} />
                            </div>
                            {tMyAccount("emailAddress.label")}:
                        </div>
                        <div className={"font-medium"}>{email ? email : "N/A"}</div>
                    </div>
                    <div className={"mt-5 flex"}>
                        <div className={"flex min-w-[160px] items-center"}>
                            <div className={"b-flex-center mr-3 h-[20px] w-[20px]"}>
                                <TwitterSvg width={18} height={14} className={"fill-[#546F7A]"} />
                            </div>
                            {tMyAccount("twitterAccount.label")}:
                        </div>
                        <div className={"font-medium"}>
                            {twitter ? (
                                <a
                                    href={formatTwitterLink(twitter.username)}
                                    className={"flex items-center hover:text-accent-1"}
                                    target={"_blank"}
                                    rel={"noreferrer"}
                                >
                                    {twitter.username} <CheckCircleSvg width={15} height={15} className={"ml-[10px]"} />
                                </a>
                            ) : (
                                "N/A"
                            )}
                        </div>
                    </div>
                    <div className={"mt-5 flex"}>
                        <div className={"flex min-w-[160px] items-center"}>
                            <div className={"b-flex-center mr-3 h-[20px] w-[20px]"}>
                                <DiscordSvg width={17} height={19} className={"fill-[#546F7A]"} />
                            </div>
                            {tMyAccount("discordAccount.label")}:
                        </div>
                        <div className={"font-medium"}>
                            {discord ? (
                                <a
                                    href={`https://discordapp.com/users/${discord.id}`}
                                    className={"hover:text-accent-1"}
                                    target={"_blank"}
                                    rel={"noreferrer"}
                                >
                                    {discord.username}
                                </a>
                            ) : (
                                "N/A"
                            )}
                        </div>
                    </div>
                    {/*<div className={"mt-[5px] flex font-secondary text-xs font-normal leading-5"}>
                            <div className={" min-w-[140px] text-neutral-1-170"}>
                                {tMyAccount("telegramAccount.label")}:
                            </div>
                            <div className={"text-neutral-1-100"}>
                                {telegram ? (
                                    <a
                                        href={`https://t.me/${telegram.username}`}
                                        className={"hover:text-accent-1"}
                                        target={"_blank"}
                                        rel={"noreferrer"}
                                    >
                                        {telegram.username}
                                    </a>
                                ) : (
                                    "N/A"
                                )}
                            </div>
                        </div>*/}
                </div>
            </div>
            <div className={"mt-5 hidden justify-center lg:mt-0 lg:flex"}>
                <button
                    onClick={onEdit}
                    className={"b-flex-center h-[23px] w-[87px] rounded-[5px] bg-[#D7D7D7] text-14 hover:text-accent-1"}
                >
                    {tCommon("button.modify")}
                    <PencilSvg width={13} height={13} className={"ml-2"} />
                </button>
            </div>
        </div>
    )
}
